import type { Ref } from "preact/compat";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

export function calculateScrollPercent(
  scrollAmount: number,
  ref: Ref<HTMLUListElement>,
  containerId: string,
) {
  if (document) {
    const container = document.getElementById(containerId);
    if (container && ref && "current" in ref && ref.current) {
      const vw = typeof document !== "undefined" ? container.offsetWidth : 0;
      const objectWidth = ref?.current?.scrollWidth ?? 0;
      return (scrollAmount / (objectWidth - vw)) * 100;
    }
  }
  return 0;
}

export function useInfiniteCarousel<T>(items: Array<T>, containerId: string) {
  const wrapper = useRef<HTMLUListElement>(null);
  const [disabled, setDisabled] = useState(false);
  const [scrollableItemsCount] = useState<number>(items.length + 2);
  const component = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [slides] = useState([items[items.length - 1], ...items, items[0]]);

  const [initialized, setInitialized] = useState(false);

  const [initializedManualNavigation, setInitializedManualNavigation] =
    useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [activeSlide, setActiveSlide] = useState(1);

  const [mouseDown, setMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    function handleResize() {
      setWrapperWidth(wrapper.current?.scrollWidth ?? 0);
    }

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const totalSlides = scrollableItemsCount ?? 1;
    const imageWidth = wrapperWidth / totalSlides;

    // Scroll to the second slide when the component is mounted
    if (container.current) {
      container.current.style.scrollBehavior = "auto";
      container.current.scrollLeft = imageWidth;
      container.current.style.scrollBehavior = "smooth";
      setTimeout(() => setInitialized(true), 500);
    }
  }, [container, wrapperWidth, scrollableItemsCount, setInitialized]);

  useEffect(() => {
    if (!initialized) return;
    const totalSlides = scrollableItemsCount ?? 1;
    const imageWidth = wrapperWidth / totalSlides;
    const scrollDistancePx = wrapperWidth * scrollPercent * 0.01;
    const itemInView =
      Math.floor(scrollDistancePx / imageWidth || 0) > totalSlides - 1
        ? totalSlides - 1
        : Math.floor(scrollDistancePx / imageWidth || 0);
    setActiveSlide((state) => {
      if (isNaN(itemInView)) return state;
      return itemInView;
    });
  }, [scrollPercent, wrapperWidth, scrollableItemsCount, initialized]);

  useEffect(() => {
    if (!initialized) return;
    const currentContainer = container.current;
    if (!currentContainer) return;

    if (activeSlide === 0 || activeSlide === scrollableItemsCount - 1) {
      setDisabled(true);
      const imageWidth = wrapperWidth / scrollableItemsCount;
      // Disable smooth scrolling
      currentContainer.style.scrollBehavior = "auto";
      setTimeout(() => {
        if (activeSlide === 0) {
          // Scroll to the second to last slide
          currentContainer.scrollLeft = imageWidth * (scrollableItemsCount - 2);
        } else if (activeSlide === scrollableItemsCount - 1) {
          // Scroll to the second slide
          currentContainer.scrollLeft = imageWidth;
        }
        setDisabled(false);
        currentContainer.style.scrollBehavior = "smooth";
      }, 500);
    }
  }, [activeSlide, container, wrapperWidth, initialized]);

  useEffect(() => {
    setScrollPercent(
      calculateScrollPercent(horizontalOffset, wrapper, containerId),
    );
  }, [horizontalOffset]);

  useEffect(() => {
    const currentContainer = container.current;

    const handleWindowScroll = () => {
      setHorizontalOffset(() => {
        return currentContainer?.scrollLeft ?? 0;
      });
    };

    if (currentContainer) {
      currentContainer.addEventListener("scroll", handleWindowScroll);
    }
    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener("scroll", handleWindowScroll);
      }
    };
  }, []);

  return useMemo(
    () => ({
      slides,
      wrapper,
      component,
      container,
      activeSlide,
      disabled,
      setActiveSlide,
      initializedManualNavigation,
      scrollPercent,
      handleNavigate: (slideIdx: number, autoNav?: boolean) => () => {
        if (!autoNav) {
          setInitializedManualNavigation(true);
        }
        const totalSlides = scrollableItemsCount ?? 1;
        const imageWidth = wrapperWidth / totalSlides;
        function calculateAmountToScroll() {
          const distanceToSlide = slideIdx - activeSlide;
          const amountToScroll = imageWidth * distanceToSlide;
          return amountToScroll;
        }

        container.current?.scrollBy(calculateAmountToScroll(), 0);
      },
      startDragging: function (e: MouseEvent) {
        setInitializedManualNavigation(true);
        if (container.current) {
          container.current.style.cursor = "grabbing";
        }
        setMouseDown(true);
        setStartX(e.pageX - (container?.current?.offsetLeft ?? 0));
        setScrollLeft(container?.current?.scrollLeft ?? 0);
      },
      stopDragging: function () {
        if (container.current) {
          container.current.style.cursor = "grab";
        }
        setMouseDown(false);
      },
      handleMouseMove: function (e: MouseEvent) {
        e.preventDefault();
        if (!mouseDown) return;
        if (container.current) {
          const x = e.pageX - container.current.offsetLeft;
          const scroll = (x - startX) * 3;
          container.current.scrollLeft = scrollLeft - scroll;
        }
      },
    }),
    [
      slides,
      wrapper,
      component,
      container,
      activeSlide,
      wrapperWidth,
      scrollPercent,
      disabled,
      activeSlide,
      setActiveSlide,
      setMouseDown,
      setStartX,
      setScrollLeft,
      mouseDown,
      setHorizontalOffset,
      initializedManualNavigation,
      setInitializedManualNavigation,
    ],
  );
}
